import { z } from "zod"

/**
 * Центр помощи и система обращений (Э5, fd-support-01).
 *
 * Две независимые сущности:
 *   1. FAQ-база знаний — статьи «вопрос/ответ», сгруппированные по категориям.
 *      Публичная выдача отдаёт только опубликованные, админка управляет всеми.
 *   2. Обращения (тикеты) — любой авторизованный пользователь (покупатель или
 *      продавец) открывает обращение и ведёт переписку с поддержкой. Статус
 *      ведёт тикет по жизненному циклу open → in_progress → resolved → closed.
 */

// --- FAQ --------------------------------------------------------------------

/** Кому адресована статья: всем, только покупателям (витрина) или только
 *  продавцам (кабинет). */
export const FaqAudienceSchema = z.enum(["all", "buyers", "sellers"])
export type FaqAudience = z.infer<typeof FaqAudienceSchema>

/** Статья базы знаний. */
export const FaqArticleSchema = z.object({
  id: z.string(),
  category: z.string(),
  question: z.string(),
  answer: z.string(),
  sortOrder: z.number().int(),
  published: z.boolean(),
  /** Адресат статьи. Отсутствует у старых ответов — читать как "all". */
  audience: FaqAudienceSchema.optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type FaqArticle = z.infer<typeof FaqArticleSchema>

/** Группа статей одной категории — единица публичной выдачи. */
export const FaqCategoryGroupSchema = z.object({
  category: z.string(),
  articles: z.array(FaqArticleSchema),
})
export type FaqCategoryGroup = z.infer<typeof FaqCategoryGroupSchema>

/** Публичная выдача FAQ — статьи, сгруппированные по категориям. */
export const FaqListSchema = z.object({
  groups: z.array(FaqCategoryGroupSchema),
})
export type FaqList = z.infer<typeof FaqListSchema>

/** Категории FAQ отдельной выдачей — источник чипов над аккордеоном.
 *  Чипы в макете стоят всегда, в том числе пока поиск ничего не нашёл, поэтому
 *  список категорий нельзя выводить из отфильтрованной выдачи статей. */
export const FaqCategoryListSchema = z.object({
  categories: z.array(z.string()),
})
export type FaqCategoryList = z.infer<typeof FaqCategoryListSchema>

/** Создание статьи (админ). */
export const CreateFaqArticleSchema = z.object({
  category: z.string().trim().min(1).max(120).default("general"),
  question: z.string().trim().min(1).max(500),
  answer: z.string().trim().min(1).max(10000),
  sortOrder: z.number().int().default(0),
  published: z.boolean().default(false),
  audience: FaqAudienceSchema.default("all"),
})
export type CreateFaqArticleRequest = z.infer<typeof CreateFaqArticleSchema>

/** Частичное обновление статьи (админ). */
export const UpdateFaqArticleSchema = z
  .object({
    category: z.string().trim().min(1).max(120),
    question: z.string().trim().min(1).max(500),
    answer: z.string().trim().min(1).max(10000),
    sortOrder: z.number().int(),
    published: z.boolean(),
    audience: FaqAudienceSchema,
  })
  .partial()
export type UpdateFaqArticleRequest = z.infer<typeof UpdateFaqArticleSchema>

// --- Обращения (тикеты) -----------------------------------------------------

export const SupportTicketStatusSchema = z.enum(["open", "in_progress", "resolved", "closed"])
export type SupportTicketStatus = z.infer<typeof SupportTicketStatusSchema>

export const SupportTicketAuthorRoleSchema = z.enum(["user", "support"])
export type SupportTicketAuthorRole = z.infer<typeof SupportTicketAuthorRoleSchema>

/** Вложение к сообщению обращения: ссылка в медиа-бакете плюс имя файла,
 *  под которым его отправили (сам бакет хранит файл под сгенерированным id). */
export const SupportAttachmentSchema = z.object({
  url: z.string().min(1).max(2000),
  name: z.string().max(300).default(""),
  kind: z.enum(["image", "file"]).default("file"),
})
export type SupportAttachment = z.infer<typeof SupportAttachmentSchema>

/** Сообщение в треде обращения. */
export const SupportTicketMessageSchema = z.object({
  id: z.string(),
  ticketId: z.string(),
  authorUserId: z.string(),
  authorRole: SupportTicketAuthorRoleSchema,
  /** Имя автора — для оформления переписки на фронте (может отсутствовать). */
  authorName: z.string().optional(),
  body: z.string(),
  /** Вложения реплики. Пустой массив, а не отсутствие поля, — клиенту не нужно
   *  отличать «нет вложений» от «старая запись». */
  attachments: z.array(SupportAttachmentSchema).default([]),
  createdAt: z.string(),
})
export type SupportTicketMessage = z.infer<typeof SupportTicketMessageSchema>

/** Сводка обращения — строка списка. */
export const SupportTicketSchema = z.object({
  id: z.string(),
  userId: z.string(),
  /** Имя автора обращения — для общего списка поддержки. */
  userName: z.string().optional(),
  /** Кто пишет: продавец или покупатель. Поддержке это нужно до открытия
   *  треда — вопросы продавца о выплатах и покупателя о заказе разбирают разные
   *  люди. Заполняется только в выдаче для консоли. */
  userRole: z.enum(["buyer", "seller", "staff"]).optional(),
  /** Контакты автора (email или телефон) — для консоли. */
  userContact: z.string().optional(),
  /** Номер обращения из столбца «Номер» — то, что продавец диктует поддержке.
   *  Внутренний id для этого не годится. */
  number: z.number().int().positive(),
  subject: z.string(),
  category: z.string(),
  status: SupportTicketStatusSchema,
  /** Число сообщений в треде. */
  messageCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastMessageAt: z.string(),
})
export type SupportTicket = z.infer<typeof SupportTicketSchema>

/** Деталь обращения — сводка плюс весь тред. */
export const SupportTicketDetailSchema = SupportTicketSchema.extend({
  messages: z.array(SupportTicketMessageSchema),
})
export type SupportTicketDetail = z.infer<typeof SupportTicketDetailSchema>

/** Список обращений. */
export const SupportTicketListSchema = z.object({
  items: z.array(SupportTicketSchema),
})
export type SupportTicketList = z.infer<typeof SupportTicketListSchema>

/** Создание обращения: тема, категория и первое сообщение. */
export const CreateTicketRequestSchema = z.object({
  subject: z.string().trim().min(1).max(300),
  category: z.string().trim().min(1).max(120).default("general"),
  // optional, а не default: обращение заводят и из шлюза (чат поддержки), где
  // вложений нет и дописывать пустой массив в каждый вызов было бы шумом.
  message: z.string().trim().min(1).max(10000),
  attachments: z.array(SupportAttachmentSchema).max(10).optional(),
})
export type CreateTicketRequest = z.infer<typeof CreateTicketRequestSchema>

/** Новое сообщение в тред (и от пользователя, и от поддержки — один шейп). */
export const TicketMessageRequestSchema = z
  .object({
    // Реплика с одним лишь вложением осмысленна (продавец досылает скрин), но
    // пустую реплику без текста и без файлов принимать нельзя — отсюда superRefine.
    body: z.string().trim().max(10000).default(""),
    attachments: z.array(SupportAttachmentSchema).max(10).default([]),
  })
  .superRefine((value, ctx) => {
    if (!value.body && value.attachments.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Message body or attachment is required",
      })
    }
  })
export type TicketMessageRequest = z.infer<typeof TicketMessageRequestSchema>

/** Смена статуса обращения (админ/поддержка). */
export const TicketStatusRequestSchema = z.object({
  status: SupportTicketStatusSchema,
})
export type TicketStatusRequest = z.infer<typeof TicketStatusRequestSchema>
